// Diagnose slice — B5 桥5 · 后台自动诊断的偏好 + 调度运行态。
//
// 为什么独立 slice（不塞进 regen-slice 或 progress）：
//   - regen-slice 是"内容生成"领域（替换/补充题目），diagnose 是"弱项诊断"领域，
//     生命周期、计费语义、触发源都不同。混在一起会让 regen-slice 膨胀且边界模糊。
//   - progress 是 IDB 持久化的学习数据；调度态（autoDiagnoseRunning）是瞬时的，
//     属于 sessionStorage 层（刷新存活，关闭即弃）。分层不同，不能塞 progress。
//
// 两层持久化（与 regen-slice 同范式 · 仓库铁律：手写持久化，不用 zustand persist）：
//   - 偏好（autoDiagnoseEnabled / autoDiagnoseIntervalHours）→ localStorage
//     跨会话存活。用户在 Settings tab opt-in 一次，永久生效直到主动关闭。
//   - 调度运行态（autoDiagnoseRunning / lastAutoDiagnoseAttemptAt）→ sessionStorage
//     仅刷新存活。防止页面刷新期间后台诊断重复触发（防抖）；关闭浏览器自然清除。
//
// 与 progress.diagnosisPending 的关系：
//   - 本 slice 只管"调度是否在跑"（瞬时控制态）。
//   - 诊断产出的 verdict 待审记录存在 progress.diagnosisPending（IDB，随 syncCourse
//     同步）。两者解耦：slice 重置不影响已产出的 pending verdict。

import type { StateCreator } from 'zustand';

// ── Preferences (Settings tab · localStorage) ──────────────────────────────

export interface DiagnosePreferences {
  /** 后台自动诊断总开关。默认 false（铁律 3：用户显式 opt-in 才自动花钱）。
   *  用户在 Settings tab 主动开启，旁有成本提示（约每 N 小时消耗 3 点）。 */
  autoDiagnoseEnabled: boolean;
  /** 自动诊断的检查间隔。决定"距上次诊断多久才值得重新诊断"的阈值。
   *  越短越新鲜但越费点；越长越省点但诊断越陈旧。默认 6h（一天约 4 次）。 */
  autoDiagnoseIntervalHours: DiagnoseIntervalHours;
}

/** 允许的间隔枚举。受控集合，避免用户填任意值导致计费失控。 */
export const DIAGNOSE_INTERVAL_VALUES = [6, 12, 24] as const;
export type DiagnoseIntervalHours = (typeof DIAGNOSE_INTERVAL_VALUES)[number];

const DIAGNOSE_PREFS_KEY = 'examforge_diagnose_prefs';

const DEFAULT_PREFS: DiagnosePreferences = {
  autoDiagnoseEnabled: false,
  autoDiagnoseIntervalHours: 6,
};

function loadPrefs(): DiagnosePreferences {
  try {
    const raw = localStorage.getItem(DIAGNOSE_PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as Partial<DiagnosePreferences>;
    return {
      autoDiagnoseEnabled: parsed.autoDiagnoseEnabled ?? DEFAULT_PREFS.autoDiagnoseEnabled,
      autoDiagnoseIntervalHours:
        isValidInterval(parsed.autoDiagnoseIntervalHours)
          ? parsed.autoDiagnoseIntervalHours
          : DEFAULT_PREFS.autoDiagnoseIntervalHours,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function isValidInterval(v: unknown): v is DiagnoseIntervalHours {
  return typeof v === 'number' && (DIAGNOSE_INTERVAL_VALUES as readonly number[]).includes(v);
}

function savePrefs(prefs: DiagnosePreferences): void {
  try {
    localStorage.setItem(DIAGNOSE_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable (quota, private browsing).
  }
}

// ── Runtime scheduling state (sessionStorage) ──────────────────────────────

export interface DiagnoseSlice {
  // ── Preferences (localStorage) ───────────────────────────────────────────
  diagnosePreferences: DiagnosePreferences;

  // ── Runtime scheduling state (sessionStorage) ───────────────────────────
  /** 后台自动诊断当前是否在跑。防止重入（同一时刻只跑一个诊断）。
   *  sessionStorage 持久化：刷新后仍知"刚才有个诊断在跑"，避免重复触发。 */
  autoDiagnoseRunning: boolean;
  /** 上次自动诊断尝试的时间戳（ms epoch）。防抖用：避免在间隔边界抖动反复跑。
   *  注意：这是"尝试"时间，不是"成功"时间——失败也记，防止失败时无限重试。
   *  成功时间的权威源是 progress.diagnosis.verdict.diagnosedAt（服务端注入）。 */
  lastAutoDiagnoseAttemptAt: number | null;

  // ── Actions ──────────────────────────────────────────────────────────────
  /** 更新单个偏好键。立即落 localStorage。 */
  setDiagnosePreference: <K extends keyof DiagnosePreferences>(
    key: K,
    value: DiagnosePreferences[K],
  ) => void;
  /** 标记后台诊断开始/结束。开始时落 sessionStorage，结束时清。 */
  setAutoDiagnoseRunning: (running: boolean) => void;
  /** 记录一次自动诊断尝试（成功或失败均记）。落 sessionStorage 防抖。 */
  recordAutoDiagnoseAttempt: () => void;
  /** 从 sessionStorage 恢复调度态（App/CourseShell mount 时调用一次）。 */
  restoreDiagnoseFromSession: () => void;
}

const DIAGNOSE_SESSION_KEY = 'examforge_diagnose_state';

/** sessionStorage 持久化白名单（排除函数；只存运行态标量）。 */
const PERSISTABLE_KEYS: ReadonlyArray<keyof DiagnoseSlice> = [
  'autoDiagnoseRunning',
  'lastAutoDiagnoseAttemptAt',
];

function persistRuntimeToSession(state: Partial<DiagnoseSlice>): void {
  try {
    const toSave: Record<string, unknown> = {};
    let hasAny = false;
    for (const key of PERSISTABLE_KEYS) {
      if (key in state) {
        toSave[key] = (state as Record<string, unknown>)[key];
        hasAny = true;
      }
    }
    // 只在 running 时持久化；idle 时清除（与 regen-slice ACTIVE_STATUSES 同语义：
    // 避免陈旧的"在跑"态在下次会话复活，导致永远不再触发）。
    if (state.autoDiagnoseRunning === false) {
      sessionStorage.removeItem(DIAGNOSE_SESSION_KEY);
      return;
    }
    if (hasAny) {
      sessionStorage.setItem(DIAGNOSE_SESSION_KEY, JSON.stringify(toSave));
    }
  } catch {
    // sessionStorage may be unavailable (private browsing quota, etc.)
  }
}

export const createDiagnoseSlice: StateCreator<DiagnoseSlice> = (set, get) => ({
  diagnosePreferences: loadPrefs(),
  autoDiagnoseRunning: false,
  lastAutoDiagnoseAttemptAt: null,

  setDiagnosePreference: (key, value) => {
    const prefs = { ...get().diagnosePreferences, [key]: value };
    set({ diagnosePreferences: prefs });
    savePrefs(prefs);
  },

  setAutoDiagnoseRunning: (running) => {
    set({ autoDiagnoseRunning: running });
    persistRuntimeToSession({ ...get(), autoDiagnoseRunning: running });
  },

  recordAutoDiagnoseAttempt: () => {
    const now = Date.now();
    set({ lastAutoDiagnoseAttemptAt: now });
    persistRuntimeToSession({ ...get(), lastAutoDiagnoseAttemptAt: now });
  },

  restoreDiagnoseFromSession: () => {
    try {
      const raw = sessionStorage.getItem(DIAGNOSE_SESSION_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as Partial<DiagnoseSlice>;
      // 若上次会话残留 autoDiagnoseRunning=true（页面在诊断中途被关闭），
      // 复活为 false——in-flight 的 fetch 已随页面关闭终止，无法 resume，
      // 强制重置为 idle 让下次调度重新触发。lastAutoDiagnoseAttemptAt 保留
      // 用于防抖（避免关闭-重开后立刻又跑一次）。
      set({
        autoDiagnoseRunning: false,
        lastAutoDiagnoseAttemptAt:
          typeof saved.lastAutoDiagnoseAttemptAt === 'number'
            ? saved.lastAutoDiagnoseAttemptAt
            : null,
      });
      // 落盘修正后的状态（running=false 会触发清除）。
      persistRuntimeToSession({ autoDiagnoseRunning: false });
    } catch {
      // Corrupt sessionStorage entry — clear and move on.
      try { sessionStorage.removeItem(DIAGNOSE_SESSION_KEY); } catch { /* noop */ }
    }
  },
});
